import React, { useMemo } from 'react';
import { useQuery, useMutation } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Dropdown, Tag } from 'antd';
import { PlusOutlined, DeleteFilled, EditFilled, CopyOutlined, LinkOutlined, PlayCircleOutlined, StopOutlined, EyeOutlined, FileSyncOutlined } from '@ant-design/icons';
import copy from 'copy-to-clipboard';

import { EDIT_SURVEY } from 'shared/constants/permissions';
import { DISTRIBUTION_STATUS } from 'shared/constants/distribution';
import DistributionStatusTag from 'components/DistributionStatusTag';
import LanguageTag from 'components/LanguageTag';
import SurveyTitle from 'components/SurveyTitle';
import DeviceTypeIcons from 'components/DeviceTypeIcons';
import Table from 'components/Table';

import globalMessages from 'translations/messages';

import useGlobalContext from 'hooks/useGlobalContext';
import { getValues } from './defaultValues';
import createMutation from './createDistribution.gql';
import deleteMutation from './deleteDistributions.gql';
import copyMutation from './copyDistribution.gql';
import startMutation from './startDistribution.gql';
import stopMutation from './stopDistribution.gql';
import moveMutation from './moveDistribution.gql';

import messages from './messages';
import useCustomer from '../../../../hooks/useCustomer';
import { getDistributionQueryVariables, skipDistributionQuery } from './queryVariables';

const { ACTIVE, SCHEDULED, MISSING_POLICY, MISSING_LICENSE, LICENSE_LIMIT } = DISTRIBUTION_STATUS;

const QUERY = gql`
  query ($customerId: ID!, $surveyId: ID, $surveyGroupId: ID) {
    distributions(surveyId: $surveyId, surveyGroupId: $surveyGroupId) {
      id
      startDate
      endDate
      container
      containerType
      trigger
      delay
      portion
      theme
      deviceTypes
      website { id }
      websiteLanguageId
      survey { id title version type language languageName customer { id personalDataController } }
      legacySurveyUrl
      status
    }
    websites(customerId: $customerId) {
      id
      title
      url
      language
      languageName
      additionalLanguages {
        id
        language
        languageName
        url
      }
      dataSource {
        id
        activeLicense {
          id
          license {
            id
            title
          }
          modules {
            id
            title          
          }
        }
      }
    }
    config { survey { baseUrl } }
  }
`;

function getWebsitePreviewLink(url, id) {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.append('extellio_debug', true);
    parsedUrl.searchParams.append('extellio_preview', id);
    return parsedUrl.href;
  } catch (e) {
    return '';
  }
}

function openPreview(url) {
  window.open(
    url,
    '_blank',
    'toolbar=no,scrollbars=yes,resizable=no,top=300,left=500,width=800,height=600',
  );
}

const renderMessage = (text, dist) => messages[text] && <FormattedMessage {...messages[text]} values={dist} />;

const renderWebsite = (websiteLanguage) => (websiteLanguage ? (
  <>
    <LanguageTag {...websiteLanguage} />
    {websiteLanguage.url}
  </>
) : null);

const renderSurveyName = (_, dist) => (
  <>
    {dist.legacySurveyUrl && <Tag color="warning"><FormattedMessage {...globalMessages.legacy} /></Tag>}
    <SurveyTitle survey={dist.survey} />
  </>
);

const getColumns = () => [
  {
    dataIndex: 'survey',
    title: <FormattedMessage {...globalMessages.survey} />,
    render: renderSurveyName,
  },
  {
    dataIndex: 'websiteLanguage',
    title: <FormattedMessage {...globalMessages.website} />,
    render: renderWebsite,
  },
  {
    dataIndex: 'startDate',
    title: <FormattedMessage {...messages.start} />,
    type: 'relativeDate',
  },
  {
    dataIndex: 'endDate',
    title: <FormattedMessage {...messages.end} />,
    type: 'relativeDate',
  },
  {
    dataIndex: 'trigger',
    title: <FormattedMessage {...messages.trigger} />,
    render: renderMessage,
  },
  {
    dataIndex: 'container',
    title: <FormattedMessage {...messages.container} />,
    render: renderMessage,
  },
  {
    dataIndex: 'portion',
    title: <FormattedMessage {...messages.portion} />,
  },
  {
    dataIndex: 'deviceTypes',
    title: <FormattedMessage {...messages.deviceTypes} />,
    render: (deviceTypes) => <DeviceTypeIcons deviceTypes={deviceTypes} />,
  },
  {
    dataIndex: 'status',
    title: <FormattedMessage {...globalMessages.status} />,
    align: 'center',
    width: 100,
    render: (status, dist) => <DistributionStatusTag type={dist.survey?.type} status={status} showTooltip />,
  },
];

const EditDistributions = ({ surveyId, surveyGroupId, surveyGroup, customerId, goTo }) => {
  const { formatMessage: $t } = useIntl();
  const { hasCustomerPermission } = useCustomer();
  const variables = getDistributionQueryVariables({
    customerId,
    surveyId,
    surveyGroupId,
    surveyGroup,
  });
  const {
    refetch,
    loading,
    data,
  } = useQuery(QUERY, {
    variables,
    skip: skipDistributionQuery(variables),
  });

  const [createDistribution, { loading: creating }] = useMutation(createMutation, { onCompleted: refetch });
  const [deleteDistribution] = useMutation(deleteMutation, { onCompleted: refetch });
  const [copyDistribution] = useMutation(copyMutation, { onCompleted: refetch });
  const [startDistribution] = useMutation(startMutation);
  const [stopDistribution] = useMutation(stopMutation);
  const [moveDistribution] = useMutation(moveMutation, { onCompleted: refetch });
  const { modal } = useGlobalContext();

  const getUrl = ({ id }, preview = true) => `${data?.config?.survey?.baseUrl}d/${id}/${preview ? '?isPreview=true' : ''}`;

  const onDelete = (id) => {
    modal.confirm({
      title: $t(globalMessages.confirmDelete),
      okText: $t(globalMessages.delete),
      okType: 'danger',
      onOk: () => deleteDistribution({ variables: { ids: [id] } }),
    });
  };

  const items = useMemo(() => data?.distributions?.map(d => {
    const dist = getValues(d);
    const website = data?.websites.find(w => w.id === d.website?.id);

    const websiteLanguage = (d.websiteLanguageId && website?.additionalLanguages.find(l => l.id === d.websiteLanguageId)) || website;

    return {
      ...d,
      ...dist,
      container: dist.container === 'popupLayer'
        ? dist.containerType
        : dist.container,
      portion: `${dist.portion}%`,
      otherSurveys: surveyGroup?.children?.filter(s => s.id !== d.survey.id),
      website,
      websiteLanguage,
    };
  }), [data]);

  const columns = getColumns();

  const previewMenuItems = dist => {
    const menuItems = [{
      key: 'popup',
      label: <FormattedMessage {...messages.popupWindow} />,
      onClick: () => openPreview(getUrl(dist, true)),
    }];

    if (dist.websiteLanguage?.url) {
      menuItems.push({
        key: 'website',
        label: (
          <a href={getWebsitePreviewLink(dist.websiteLanguage?.url, dist.id)} target="_blank" type="link">
            {dist.websiteLanguage?.title}
          </a>
        ),
      });
    }

    return menuItems;
  };

  const actionsMenuItems = hasCustomerPermission(EDIT_SURVEY) && (({ id, status, otherSurveys }) => [{
    key: 'start',
    label: <FormattedMessage {...messages.startNow} />,
    icon: <PlayCircleOutlined />,
    onClick: () => startDistribution({ variables: { id } }),
    visible: ![ACTIVE, MISSING_POLICY, MISSING_LICENSE, LICENSE_LIMIT].includes(status),
  }, {
    key: 'stop',
    label: <FormattedMessage {...messages.stop} />,
    icon: <StopOutlined />,
    onClick: () => stopDistribution({ variables: { id } }),
    visible: [ACTIVE, SCHEDULED].includes(status),
  }, {
    key: 'duplicate',
    label: <FormattedMessage {...globalMessages.duplicate} />,
    icon: <CopyOutlined />,
    onClick: () => copyDistribution({ variables: { id } }),
  }, {
    key: 'move',
    label: <FormattedMessage {...messages.move} />,
    icon: <FileSyncOutlined />,
    children: otherSurveys?.map(s => ({
      key: s.id,
      label: <SurveyTitle survey={s} />,
      onClick: () => moveDistribution({ variables: { id, surveyId: s.id } }),
    })),
    visible: otherSurveys?.length > 0,
  }, {
    key: 'copyLink',
    label: <FormattedMessage {...messages.copyLink} />,
    icon: <LinkOutlined />,
    onClick: () => copy(getUrl({ id }, false)),
  }, {
    key: 'delete',
    label: <FormattedMessage {...globalMessages.delete} />,
    icon: <DeleteFilled />,
    onClick: () => onDelete(id),
    danger: true,
  }]);

  const actions = (record) => (
    <>
      <Dropdown placement="bottomRight" trigger={['click']} menu={{ items: previewMenuItems(record) }}>
        <Button icon={<EyeOutlined />}>
          <FormattedMessage {...messages.preview} />
        </Button>
      </Dropdown>
      <Button type="primary" icon={<EditFilled />} onClick={() => goTo({ step: 'distribution', distributionId: record.id, distributionStep: 'style' })}>
        <FormattedMessage {...globalMessages.edit} />
      </Button>
    </>
  );

  return (
    <Table
      loading={{
        size: 'large',
        spinning: loading,
      }}
      emptyMessage={<FormattedMessage {...messages.emptyMsg} />}
      columns={columns}
      dataSource={items}
      actions={actions}
      actionsMenuItems={actionsMenuItems}
      footerActions={(
        <Button type="primary" onClick={() => createDistribution({ variables: { surveyId, customerId } })} loading={creating} icon={<PlusOutlined />} style={{ marginBottom: '1em' }}>
          <FormattedMessage {...messages.addDistribution} />
        </Button>
        )}
    />
  );
};

export default EditDistributions;
